import { FC, useEffect } from 'react'
import { Form, json, replace, useActionData } from 'react-router-dom'

// COMPONENTS
import * as Icons from '@icons'
import TextField from '@components/atoms/textfield'
import Button from '@components/atoms/button'

// TYPES
import type { RouteObject } from 'react-router-dom'

// UTILITIES
import { useEmailIdContext } from '..'
import { useInput } from '@hooks/inputs'
import { createAuthenticatedLoader, useLoader } from '@utils/loaders'
import { createAction } from '@utils/actions'
import { isNonEmptyString } from '@utils/type-checkers'
import { isFailedResponse } from '@apis'


const Component: FC<{}> = () => {
    const resource = useLoader<typeof loader>()
    const { setToast } = useEmailIdContext()
    const otpProps = useInput()[1]
    const data = useActionData()

    useEffect(() => {
        console.log(data)
        if (data != null) {
            let message = isNonEmptyString(data) ? data : 'An unexpected Error occured'
            if (message === 'This verification has expired. You must create a new one.')
                message = 'This code has expired. We have sent you a new one.'

            setToast({ type: 'error', text: message });
        }
    }, [setToast, data])

    useEffect(() => {
        const verification = resource.verification
        console.log({ verification })

        if (verification?.strategy === 'email_link' && verification?.status !== 'verified') {
            setToast({ type: 'error', text: "Issue verifying your email." })
        }
    }, [setToast, resource])

    if (resource.verification?.status === 'verified') return (
        <div className="flex flex-col items-center" style={{ gap: 'inherit', textAlign: 'center' }}>
            <h2>Verify E-mail Address</h2>
            <Icons.Check type="bordered" tint="var(--splitcard-green-500)" width="4.8rem" height="4.8rem" style={{ margin: '2rem 0rem' }} />
            <h2>{resource.emailAddress} has been verified!</h2>
            <Button theme="primary" to="/account/profile" navOptions={{ replace: true }} style={{ alignSelf: 'stretch' }}>Finish</Button>
        </div>
    )

    else return (
        <Form className="flex flex-col" style={{ gap: 'inherit' }} method="POST">
            <h4>Verify E-mail Address</h4>
            {resource.verification?.strategy === 'email_link' && <>
                <p>There was an issue verifying the following email address: <strong>{resource.emailAddress}</strong>. Send another link to try verifying again.</p>
                <small>Didn't get the link? Check spam/junk or resend it</small>
                <Button type="submit" theme="primary">Resend</Button>
            </>}
            {resource.verification?.strategy === 'email_code' && <>
                <p>A verification code has been sent to {resource.emailAddress}. Enter the 6-digit code below!</p>
                <TextField {...otpProps} type="text" name="otp" placeholder="Verification Code" autoComplete="one-time-code" autoCorrect="off" maxLength={6} required />
                <small>Didn't get the code? Check spam/junk or resend it</small>
                <Button type="submit" theme="primary">Continue</Button>
            </>}
        </Form>
    )
}

const loader = createAuthenticatedLoader(async ({ params }, Clerk) => {
    const emailId = params['emailId'] || ''
    const resource = Clerk.user?.emailAddresses.find(({ id }) => id === emailId)

    if (!!resource) {
        if (resource.verification.strategy === "email_link") {

            if (resource.verification.status === 'verified')
                return resource

            try {
                await Clerk.handleEmailLinkVerification({})
                resource.verification.status = 'verified'
            } catch (error) {
                console.log(error)
                resource.verification.status = 'expired'
            }

            return resource
        }
        if (resource.verification.status !== 'verified')
            await resource.prepareVerification({ strategy: 'email_code' })
        return resource
    } else return replace('/account/profile')
})

const action = createAction(async ({ request, params }) => {
    try {
        const data = await request.formData()
        const otp = data.get('otp')
        const emailId = params['emailId'] || ''
        const resource = window.Clerk?.user?.emailAddresses.find(({ id }) => id === emailId)

        if (!!resource) {
            if (resource.verification?.strategy === 'email_code' && isNonEmptyString(otp)) {
                const res = await resource.attemptVerification({ code: otp })
                console.log(res)
                if (res.verification.status === 'verified') return json(null, 200)
                else throw new Error(`An issue occured during verification. Failed with status: ${res.verification.status}`)
            }

            if (resource.verification?.strategy === "email_link") {
                const baseUrl = import.meta.env.VITE_VIRTUAL_CARD_BASE_URL.replace(".api", "").replace("api", "")
                const redirectUrl = `${baseUrl}/account/profile/email/${resource.id}/verify`

                await resource.prepareVerification({ strategy: 'email_link', redirectUrl })
                return json(null, 200)
            }
        }
    } catch (error) {
        console.error(error)
        if (isFailedResponse(error) || error instanceof Error) return json(error.message);
        else return json('An unexpected Error occured')
    }
})

const VerifyEmailRoute: RouteObject = {
    path: 'verify',
    Component,
    loader,
    action,
}

export { VerifyEmailRoute } 
